import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import {
    animationFadeIn,
    animationFadeOut,
    animationZoomIn,
    animationZoomOut,
} from "../../../common/animations";
import { H2 } from "../heading/H2";
import { H4 } from "../heading/H4";
import { Button, Props as ButtonProps } from "../inputs/Button";

export type Action = Omit<React.HTMLAttributes<HTMLButtonElement>, "as"> &
    Omit<ButtonProps, "onClick"> & {
        confirmation?: boolean;
        onClick: () => void | boolean | Promise<boolean>;
    };

export interface Props {
    padding?: string;
    maxWidth?: string;
    maxHeight?: string;

    disabled?: boolean;
    transparent?: boolean;
    nonDismissable?: boolean;
    fullscreen?: boolean;

    actions?: Action[];
    onClose?: (force: boolean) => void;

    signal?: "close" | "confirm" | "force";
    registerOnClose?: (fn: () => void) => () => void;
    registerOnConfirm?: (fn: () => void) => () => void;

    title?: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
}

const Base = styled.div<{ closing?: boolean; isFullScreen?: boolean }>`
    top: 0;
    left: 0;
    z-index: 9999;
    position: fixed;
    user-select: none;
    animation-duration: 0.2s;
    animation-fill-mode: forwards;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--foreground);
    background: rgba(0, 0, 0, 0.95);
    overscroll-behavior: contain;
    outline: none;

    ${(props) =>
        props.isFullScreen
            ? css`
                  width: 100vw;
                  height: 100vh;
                  animation: ${
                      props.closing
                          ? css`
                                ${animationFadeOut} 0.2s forwards
                            `
                          : "none"
                  };
              `
            : css`
                  width: 100%;
                  height: 100%;
                  max-height: 100%;
              `}
    ${(props) =>
        props.closing
            ? css`
                  animation-name: ${animationFadeOut};

                  > div {
                      animation-name: ${animationZoomOut};
                  }
              `
            : css`
                  animation-name: ${animationFadeIn};
              `}
`;

const Container = styled.div<
    Pick<Props, "transparent" | "maxWidth" | "maxHeight" | "fullscreen"> & {
        actions: boolean;
        closing: boolean;
    }
>`
    display: flex;
    flex-direction: column;

    will-change: transform, opacity;
    transform: translate3d(0, 0, 0);

    animation-name: ${(props) => (props.closing ? animationZoomOut : animationZoomIn)};
    animation-duration: 0.25s;
    animation-timing-function: cubic-bezier(0.3, 0.3, 0.18, 1.1);

    ${(props) =>
        props.fullscreen
            ? css`
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  margin: auto;
                  border-radius: 0 !important;

                  max-width: none !important;
                  max-height: none !important;

                  /* Центрируем точку трансформации для анимаций */
                  transform-origin: center center;
              `
            : css`
                  /* Для стандартной модалки оставляем старые ограничения */
                  margin: 20px;
                  min-height: 200px;
                  max-width: min(
                      calc(100vw - 20px),
                      ${(props) => props.maxWidth ?? "450px"}
                  );
                  max-height: min(
                      calc(100vh - 20px),
                      ${(props) => props.maxHeight ?? "650px"}
                  );
                  ${
                      !props.maxWidth &&
                      css`
                          width: 100%;
                      `
                  }
              `}

    ${(props) =>
        !props.maxWidth &&
        css`
            width: 100%;
        `}

    ${(props) =>
        !props.transparent &&
        css`
            overflow: hidden;
            background: var(--secondary-header);
            border-radius: var(--border-radius);
        `}
`;

const Title = styled.div`
    padding: 1rem;
    flex-shrink: 0;
    word-break: break-word;
    gap: 8px;
    display: flex;
    flex-direction: column;
`;

const Content = styled.div<Pick<Props, "transparent" | "padding">>`
    flex-grow: 1;
    padding-top: 0;
    padding: ${(props) => props.padding ?? "0 1rem 1rem"};

    overflow-y: auto;
    font-size: 0.9375rem;

    display: flex;
    flex-direction: column;

    ${(props) =>
        !props.transparent &&
        css`
            background: var(--secondary-header);
        `}
`;

const Actions = styled.div`
    flex-shrink: 0;

    gap: 8px;
    display: flex;
    padding: 1rem;
    flex-direction: row-reverse;

    background: var(--secondary-background);
    border-radius: 0 0 var(--border-radius) var(--border-radius);
`;

export function getCurrentModalBase() {
    return document.querySelector(
        "[data-current-modal-base]",
    ) as HTMLElement | null;
}

export const Modal: (props: Props) => JSX.Element = ({
    children,
    actions,
    disabled,
    onClose,
    title,
    description,
    nonDismissable,
    registerOnClose,
    registerOnConfirm,
    signal,
    fullscreen,
    ...props
}: Props) => {
    const [closing, setClosing] = useState(false);
    const [isReady, setIsReady] = useState(!fullscreen);

    const closeModal = useCallback(() => {
        setClosing(true);
        if (fullscreen) {
            setTimeout(async () => {
                onClose?.(true);
                if (document.fullscreenElement) {
                    try {
                        await document.exitFullscreen();
                        const fsLayer = document.getElementById("fs-layer");
                        if (fsLayer) fsLayer.style.display = "none";
                    } catch (err) {
                        console.warn(err);
                    }
                }
                // }
            }, 100);
        } else {
            // Для обычных модалок
            !closing && setTimeout(() => onClose?.(true), 200);
        }
    }, [closing, fullscreen, onClose]);

    const confirm = useCallback(async () => {
        if (await actions?.find((x) => x.confirmation)?.onClick?.()) {
            closeModal();
        }
    }, [actions]);

    useEffect(() => registerOnClose?.(closeModal), [closeModal]);
    useEffect(() => registerOnConfirm?.(confirm), [confirm]);

    useEffect(() => {
        if (signal === "confirm") {
            confirm();
        } else if (signal) {
            if (signal === "close" && nonDismissable) {
                return;
            }

            closeModal();
        }
    }, [signal]);

    // Эти два рефа нужны для проверки что клик начинается и заканчивается на overlay элементу модального окна
    const clickTarget = useRef<HTMLDivElement>(null);
    const baseElement = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fsLayer = document.getElementById("fs-layer");
        if (fullscreen && isReady && fsLayer) {
            fsLayer.focus();
        }
    }, [fullscreen, isReady]);

    useEffect(() => {
        if (!fullscreen || isReady) return;
        if (baseElement.current && !document.fullscreenElement) {
            let fsLayer = document.getElementById("fs-layer");
            if (!fsLayer) {
                fsLayer = document.createElement("div");
                fsLayer.id = "fs-layer";
                fsLayer.style.cssText =
                    "position:fixed;top:0;left:0;width:100vw;height:100vh;background:#000;z-index:999999;display:block;";
                document.body.appendChild(fsLayer);
            } else {
                fsLayer.style.display = "block";
            }
            fsLayer
                .requestFullscreen()
                .then(() => {
                    setIsReady(true);
                })
                .catch((err) => {
                    console.warn("Фуллскрин заблокирован:", err);
                    setIsReady(false);
                });
        }
    }, [fullscreen, isReady]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const fsLayer = document.getElementById("fs-layer");

            if (!document.fullscreenElement) {
                if (fsLayer) {
                    fsLayer.classList.add("fs-layer-closing");
                    fsLayer.style.display = "none";
                    setTimeout(() => {
                        fsLayer.remove();
                    }, 200);
                }
                closeModal();
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener(
                "fullscreenchange",
                handleFullscreenChange,
            );
        };
    }, [props]);

    const isFsActive = !!document.fullscreenElement;

    const portalTarget =
        isFsActive && document.getElementById("fs-layer")
            ? document.getElementById("fs-layer")!
            : document.body;

    return createPortal(
        <Base
            data-current-modal-base
            ref={baseElement}
            closing={closing}
            isFullScreen={fullscreen}
            onMouseDown={(e) => {
                const target = e.target;
                if (target === baseElement.current) {
                    clickTarget.current = target;
                } else {
                    clickTarget.current = null;
                }
            }}
            onMouseUp={(e) => {
                const target = e.target;
                if (
                    target === clickTarget.current &&
                    target === baseElement.current &&
                    !nonDismissable
                ) {
                    closeModal();
                }
                clickTarget.current = null;
            }}>
            {isReady && (
                <Container
                    {...props}
                    fullscreen={fullscreen}
                    actions={actions ? actions.length > 0 : false}

                    closing={closing}>
                    {(title || description) && (
                        <Title>
                            {title && <H2>{title}</H2>}
                            {description && <H4>{description}</H4>}
                        </Title>
                    )}
                    <Content {...props}>{children}</Content>
                    {actions && actions.length > 0 && (
                        <Actions>
                            {actions.map((x, index) => (
                                // @ts-expect-error cope
                                <Button
                                    disabled={disabled}
                                    key={index}
                                    {...x}
                                    onClick={async () => {
                                        if (await x.onClick()) {
                                            closeModal();
                                        }
                                    }}
                                />
                            ))}
                        </Actions>
                    )}
                </Container>
            )}
        </Base>,
        portalTarget,
    );
};
